import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { MedicationRequestInfo } from '@/components/common/medicationrequestinfo'
import { useUser } from '@clerk/clerk-react'
import { Suspense, useEffect, useState } from 'react'
import Filter from './filter'
import OrderSearchResults from './order-search-results'
import SearchInput from '@/components/ui/search-input'
import { resetFilterStore, useFilterStore } from '@/lib/stores/filter'
import DisplayCutoffTimeNotice from '@/components/common/cutoff-time-notice'
import { useToast } from '@/hooks/use-toast'
import { canSearchOrdersByCustomer } from '@/lib/permissions'
import { getDashboardSearchType } from '@/lib/api/orders/search'

const { updateFilteringOptions } = useFilterStore.getState()

const MAX_SEARCH_LENGTH = 255
const CUSTOMER_SEARCH_DEBOUNCE_MS = 400
// Name/email searches hit a LIKE on the orders tables — don't fire them for one or two characters.
const MIN_CUSTOMER_SEARCH_LENGTH = 3

const HELPER_TEXT = {
  orderNumber: 'Searching by order number',
  email: 'Searching by email address',
  name: 'Searching by full name'
} as const

function useDebouncedValue<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timeout)
  }, [value, delay])

  return debouncedValue
}

export default function DashboardPage() {
  const { toast } = useToast()
  const { user } = useUser()
  const canSearchByCustomer = canSearchOrdersByCustomer()

  const { filteringOptions } = useFilterStore()
  const [searchValue, setSearchValue] = useState(filteringOptions['Order Number'][0] || '')

  const trimmedSearch = searchValue.trim()
  const searchType = canSearchByCustomer && trimmedSearch ? getDashboardSearchType(trimmedSearch) : 'orderNumber'
  const debouncedSearch = useDebouncedValue(trimmedSearch, CUSTOMER_SEARCH_DEBOUNCE_MS)
  const debouncedSearchType =
    canSearchByCustomer && debouncedSearch ? getDashboardSearchType(debouncedSearch) : 'orderNumber'
  // Keeps the last results on screen (dimmed while refetching) between keystrokes instead of flashing the queue.
  const showCustomerResults =
    searchType !== 'orderNumber' &&
    debouncedSearchType !== 'orderNumber' &&
    debouncedSearch.length >= MIN_CUSTOMER_SEARCH_LENGTH

  const resetSearchFiltering = () =>
    updateFilteringOptions(options => ({
      ...options,
      'Order Number': []
    }))

  const clearSearch = () => {
    setSearchValue('')
    resetSearchFiltering()
  }

  // Picking a queue filter drops an order-number search, as the collapsible search used to.
  useEffect(() => {
    if (
      (filteringOptions['ID Verified Time'].length > 0 || filteringOptions['Shipping Method'].length > 0) &&
      filteringOptions['Order Number'].length > 0
    ) {
      setSearchValue('')
      updateFilteringOptions(options => ({ ...options, 'Order Number': [] }))
    }
  }, [filteringOptions])

  const handleSearchChange = (value: string) => {
    if (value.length > MAX_SEARCH_LENGTH) {
      toast({
        title: 'Search length error',
        description: `Searching has a maximum character limit of ${MAX_SEARCH_LENGTH}.`
      })
      return
    }

    setSearchValue(value)

    const query = value.trim()
    const nextType = canSearchByCustomer && query ? getDashboardSearchType(query) : 'orderNumber'

    if (query === '' || nextType !== 'orderNumber') {
      // Name/email results replace the queue, so the queue shouldn't stay filtered by a stale order number.
      if (filteringOptions['Order Number'].length) resetSearchFiltering()
      return
    }

    resetFilterStore()
    updateFilteringOptions(options => ({
      ...options,
      'Order Number': [query]
    }))
  }

  return (
    <section className='flex flex-col gap-8'>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-col gap-2'>
          <h2 className='capitalize text-[2rem] font-semibold leading-10 text-secondary'>
            Welcome {user?.fullName || user?.primaryEmailAddress?.emailAddress}
          </h2>

          <p className='leading-6 text-sm font-normal text-custom-neutral-500'>
            Use filters to prioritize medication requests that need action.
          </p>
        </div>

        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          onCancel={clearSearch}
          placeholder={canSearchByCustomer ? 'Search by order number, email or full name' : 'Search for order number'}
          helperText={canSearchByCustomer && trimmedSearch ? HELPER_TEXT[searchType] : undefined}
        />
      </div>

      {showCustomerResults ? (
        <OrderSearchResults searchQuery={debouncedSearch} searchType={debouncedSearchType} />
      ) : (
        <>
          <ErrorBoundary fallback='Error loading totals'>
            <Suspense>
              <MedicationRequestInfo />
              <DisplayCutoffTimeNotice />
            </Suspense>
          </ErrorBoundary>

          <Filter />
        </>
      )}
    </section>
  )
}
